import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil, Search, Trash2 } from "lucide-react";

import { studentService, type Class } from "../../services/studentService";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../../utils/exportTable";
import { ExportButtons, type ExportFormat } from "../ExportButtons";
import { DetailsModal } from "../register/DetailsModal";
import { UsageNotice } from "../register/RecordParts";
import { humanise } from "../register/registerLogic";
import { occupancy } from "./classSetupLogic";

/**
 * One class opened from the register: how full it is, who is in it and who
 * teaches it. Delete is offered only once the server says nothing depends on
 * the class; the class list can be downloaded for the class teacher.
 */
export function ClassDetails({ record, canWrite, busy, onEdit, onDelete, onClose }: {
  record: Class;
  canWrite: boolean;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const detail = useQuery({ queryKey: ["class-streams", "detail", record.id], queryFn: () => studentService.getClass(record.id) });
  const [search, setSearch] = useState("");
  const current = detail.data ?? record;
  const fill = occupancy(current.current_student_count, current.capacity);
  const learners = detail.data?.learners ?? [];
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = learners.filter((row) => words.every((word) => `${row.full_name} ${row.student_id} ${row.admission_number}`.toLowerCase().includes(word)));
  const exportList = (format: ExportFormat) => {
    const title = `${current.name} class list (${current.academic_year_name})`;
    const columns = [
      { header: "Name", accessor: (row: (typeof learners)[number]) => row.full_name },
      { header: "Student ID", accessor: (row: (typeof learners)[number]) => row.student_id },
      { header: "Admission no.", accessor: (row: (typeof learners)[number]) => row.admission_number },
      { header: "Gender", accessor: (row: (typeof learners)[number]) => humanise(row.gender) },
    ];
    const file = `class-list-${current.name.replace(/\s+/g, "-").toLowerCase()}`;
    if (format === "csv") exportAsCsv(`${file}.csv`, columns, learners);
    if (format === "excel") exportAsExcel(`${file}.xls`, title, columns, learners);
    if (format === "pdf") exportAsPdf(title, columns, learners);
  };
  const inUse = detail.data?.in_use;

  return (
    <DetailsModal
      size="3xl"
      title={current.name}
      headline={{ label: "Learners in the class", value: `${current.current_student_count} of ${current.capacity}`, note: current.available_spots >= 0 ? `${current.available_spots} ${current.available_spots === 1 ? "place" : "places"} free` : `${-current.available_spots} over capacity` }}
      status={{ label: fill.label, dot: fill.dot }}
      facts={[
        ["Level", current.level],
        ["Stream", current.section || "No stream"],
        ["Academic year", current.academic_year_name],
        ["Class teacher", current.class_teacher_name || <span className="text-amber-700">Not assigned</span>],
        ["Room", current.room || "—"],
        ["Boys / girls", `${current.boys ?? 0} / ${current.girls ?? 0}`],
      ]}
      sections={[{ title: "Notes for staff", body: current.notes }]}
      notice={
        <div className="h-2 overflow-hidden rounded-full bg-gray-100" role="img" aria-label={`${fill.percent}% full`}>
          <div className={`h-full rounded-full ${fill.bar}`} style={{ width: `${Math.min(100, fill.percent)}%` }} />
        </div>
      }
      onClose={onClose}
      footer={
        <>
          {canWrite && inUse && !Object.values(inUse).some(Boolean) && (
            <button type="button" disabled={busy} onClick={onDelete} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
          {canWrite && (
            <button type="button" className="btn btn-primary gap-1.5" onClick={onEdit}>
              <Pencil className="h-4 w-4" /> Edit
            </button>
          )}
        </>
      }
    >
      {detail.isError && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">The class's learners and teachers could not be loaded.</p>}
      <UsageNotice inUse={inUse} advice="Move its learners and teaching to another class, or rename it, instead." />

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subject teachers</h4>
        </div>
        {detail.isLoading ? (
          <div className="h-7 animate-pulse rounded bg-gray-100" />
        ) : detail.data?.subject_teachers.length ? (
          <ul className="grid gap-x-6 gap-y-1 rounded-lg border border-gray-200 px-4 py-2 text-sm sm:grid-cols-2">
            {detail.data.subject_teachers.map((row) => (
              <li key={row.id} className="flex justify-between gap-3 border-b border-gray-100 py-1 last:border-0">
                <span className="text-gray-600">{row.subject}</span>
                <span className="font-medium text-gray-900">{row.teacher}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No subject teachers are assigned to this class yet.</p>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Class list</h4>
          <ExportButtons onExport={exportList} disabled={!learners.length} what="the class list" />
        </div>
        {detail.isLoading ? (
          <div className="space-y-1.5">{Array.from({ length: 4 }).map((_, index) => <div key={index} className="h-6 animate-pulse rounded bg-gray-100" />)}</div>
        ) : learners.length ? (
          <>
            {learners.length > 8 && (
              <label className="relative block">
                <span className="sr-only">Search the class list</span>
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} className="input-compact pl-8" placeholder="Search by name or number" />
              </label>
            )}
            <div className="sheet-wrap max-h-72 overflow-y-auto">
              <table className="sheet">
                <thead>
                  <tr>
                    <th className="sheet-rownum">#</th>
                    <th>Name</th>
                    <th>Student ID</th>
                    <th>Admission no.</th>
                    <th>Gender</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row, index) => (
                    <tr key={row.id}>
                      <td className="sheet-rownum">{index + 1}</td>
                      <td className="font-medium text-gray-900">{row.full_name}</td>
                      <td className="tabular-nums">{row.student_id}</td>
                      <td className="tabular-nums">{row.admission_number}</td>
                      <td>{humanise(row.gender)}</td>
                    </tr>
                  ))}
                  {!shown.length && (
                    <tr><td colSpan={5} className="py-4 text-center text-sm text-gray-500">No learner in the class matches.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-500">No learners are in this class yet. Learners join it through admission, promotion or a class change on their record.</p>
        )}
      </section>
    </DetailsModal>
  );
}
